import React, { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  AlertCircle,
  FolderOpen,
  FileText,
  MessageSquare,
} from "lucide-react";

const ChallengeMappingUI = ({ response }: any) => {
  const [expandedCategory, setExpandedCategory] = useState<number | null>(null);
  const [expandedSentiment, setExpandedSentiment] = useState<{
    category: number;
    sentiment: number;
  } | null>(null);


  const challengeMapping = response?.challenge_mapping || [];

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.85) return 'bg-green-100 text-green-800 border-green-300';
    if (confidence >= 0.7) return '  bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 0.85) return 'High';
    if (confidence >= 0.7) return 'Medium';
    return 'Low';
  };

  const confidencebar = (confidence:number)=>{
    if (confidence >= 0.85) return 'bg-green-500 ';
    if (confidence >= 0.7) return '  bg-yellow-500 ';
    return 'bg-red-500 ';
  }

  return (
    <div className="h-[90vh] p-4 border rounded-xl border-gray-300 overflow-y-auto pn_scroller">
      <div className="max-w-5xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <FolderOpen className="w-3 h-3 text-blue-600" />
          <h1 className="text-md font-semibold text-slate-800">
            Patient Challenge Insights
          </h1>
        </div>

        {/* Challenge Categories */}
        {challengeMapping.map((challenge: any, catIndex: number) => (
          <div
            key={catIndex}
            className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden"
          >
            {/* Category Header */}
            <div
              className="p-3 flex justify-between items-center cursor-pointer hover:bg-slate-50 transition"
              onClick={() =>
                setExpandedCategory(expandedCategory === catIndex ? null : catIndex)
              }
            >
              <div>
                <h2 className="text-md font-semibold text-slate-800">
                  {challenge.category}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {challenge.sentiments.length} related insights
                </p>
              </div>
              {expandedCategory === catIndex ? (
                <ChevronDown className="w-2 h-2 text-slate-500" />
              ) : (
                <ChevronRight className="w-2 h-2 text-slate-500" />
              )}
            </div>

            {/* Expanded Sentiments */}
            {expandedCategory === catIndex && (
              <div className="bg-slate-50 border-t border-slate-200 p-3 space-y-3">
                {challenge.sentiments.map((item: any, sentIndex: number) => (
                  <div
                    key={sentIndex}
                    className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden"
                  >
                    <div
                      className="p-3 cursor-pointer hover:bg-slate-50 transition"
                      onClick={() =>
                        setExpandedSentiment(
                          expandedSentiment?.category === catIndex &&
                            expandedSentiment?.sentiment === sentIndex
                            ? null
                            : { category: catIndex, sentiment: sentIndex }
                        )
                      }
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex items-start gap-2">
                          <AlertCircle
                            className={`w-3 h-3 mt-0.5`}
                          />
                          <div>
                            <h3 className="text-sm font-semibold text-slate-800">
                              {item.sentiment}
                            </h3>
                            <p className="text-xs text-slate-500">
                              File: {item.file_name}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getConfidenceColor(
                              item.confidence
                            )}`}
                          >
                            {getConfidenceLabel(item.confidence)} Confidence
                          </span>
                          <span className="text-[10px] text-slate-500 font-mono">
                            {(item.confidence * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>

                      {/* Confidence Bar */}
                      <div className="mt-2 w-full bg-slate-200 rounded-full h-1">
                        <div
                          className={`h-1 rounded-full ${confidencebar(item.confidence)
                          }`}
                          style={{ width: `${item.confidence * 100}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Expanded Source */}
                    {expandedSentiment?.category === catIndex &&
                      expandedSentiment?.sentiment === sentIndex && (
                        <div className="px-4 pb-3 pt-2 bg-slate-50 border-t border-slate-200">
                          <p className="text-[11px] text-slate-500 font-medium uppercase mb-1">
                            Source Quote
                          </p>
                          <div className="bg-white rounded-lg p-3 border-l-4 border-blue-400">
                            <p className="text-xs text-slate-700 italic leading-relaxed">
                              “{item.source}”
                            </p>
                          </div>
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        <div className="mt-6 bg-blue-50 rounded-lg p-3 border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Tip:</strong> Click a challenge category to view detailed
            sentiments and source quotes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChallengeMappingUI;
